/**
 * Helpers for the optional Khmer translation fields on products
 * (about_item_km, how_to_use_km, ingredients_km).
 *
 * These columns are TEXT (~65,000 bytes), not varchar(5000) like their English
 * counterparts, so they must not reuse the English length rules. Khmer characters
 * take 3 bytes each in UTF-8, so the practical budget is roughly 21,000 characters.
 */

export const KHMER_TEXT_BYTE_LIMIT = 65000;

/**
 * Byte length of a string once encoded as UTF-8 — what the database actually counts.
 * @param {string} value
 * @returns {number}
 */
export const byteLength = (value) => {
	if (!value) return 0;
	if (typeof TextEncoder !== "undefined") {
		return new TextEncoder().encode(value).length;
	}
	return unescape(encodeURIComponent(value)).length;
};

/**
 * Visible characters in a rich-text value, with markup and entities stripped.
 * @param {string} html
 * @returns {number}
 */
export const plainTextLength = (html) => {
	if (!html) return 0;
	return html
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.trim().length;
};

/**
 * Quill never yields an empty string — a cleared editor still emits `<p><br></p>`.
 * Treat anything without visible text or embedded media as empty.
 * @param {string} html
 * @returns {boolean}
 */
export const isRichTextEmpty = (html) => {
	if (!html) return true;
	if (/<(img|iframe|video)\b/i.test(html)) return false;
	return plainTextLength(html) === 0;
};

/**
 * Normalise a Khmer rich-text value for the API: blank means "not translated yet",
 * which is stored as null. Never falls back to the English value.
 * @param {string} html
 * @returns {string|null}
 */
export const toNullableRichText = (html) => (isRichTextEmpty(html) ? null : html);

/**
 * Yup test body: true when the value fits the column's byte budget.
 * @param {string} html
 * @returns {boolean}
 */
export const isWithinKhmerByteLimit = (html) => byteLength(html || "") <= KHMER_TEXT_BYTE_LIMIT;
